import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import { useRouter } from 'next/router';
import { useTranslate, useCurrentUser, useFilterSelector, useSearchSelector } from '@hooks';
import { useForm } from '@dotone/react-ui-core/hooks';
import { getMktSiteCode, searchOffers } from '@actions';
import { getNameWithId } from '@dotone/react-ui-core/utils';
import { Row, Col, FormGroup, Label, Form } from 'reactstrap';
import { InputClipboard, MessageEmpty } from '@dotone/react-ui-core';
import DocsConversionStepTabset from './DocsConversionStepTabset';
import DocsTranslate from './DocsTranslate';
import { OfferSearchInput } from '../forms';

const RenderTranslate = ({ conversionStep, ...otherProps }) => {
  const { id, conversionPixelAsync, label } = conversionStep;
  return (
    <DocsTranslate
      key={id}
      tKey="javascriptPixel.conversionPixel"
      code={conversionPixelAsync}
      values={{ label }}
      {...otherProps}
    />
  );
};

const DocsJavascriptPixel = () => {
  const { t, locale } = useTranslate('docs', 'javascriptPixel');
  const dispatch = useDispatch();
  const router = useRouter();
  const { currentUser } = useCurrentUser();
  const { connectForm, watch, setValue } = useForm();
  const offerId = watch('offerId');
  const { mkt_site_id: givenMktSiteId } = router.query;

  const { data } = useSearchSelector(({ offerSearch }) => offerSearch, {
    withMktSite: true,
    active: true,
    locale,
  });
  const selectedOffer = data.find((offer) => offer.id.toString() === offerId?.toString());
  const mktSiteId = offerId ? selectedOffer?.mktSiteId : givenMktSiteId;

  const { data: mktSite } = useFilterSelector(({ mktSiteCode }) => mktSiteCode, {
    id: mktSiteId,
    locale,
    forGtm: false,
  });
  const { offer, network, browsePixelAsync } = mktSite || {};
  const { conversionSteps } = offer || {};

  useDeepCompareEffect(() => {
    if (!givenMktSiteId && currentUser.id) {
      dispatch(
        searchOffers({
          withMktSite: true,
          active: true,
          locale,
        })
      );
      if (!offerId) setValue('offerId', data[0]?.id);
    }
  }, [dispatch, offerId, data.length, currentUser.id]);

  useEffect(() => {
    if (mktSiteId && !mktSite?.id) {
      dispatch(getMktSiteCode(mktSiteId, { locale }));
    }
  }, [dispatch, mktSiteId, mktSite?.id, locale]);

  return (
    <>
      {!givenMktSiteId && data.length > 1 && (
        <Form>
          {connectForm(
            <FormGroup>
              <Label>{t('common:model.offer')}</Label>
              <OfferSearchInput active searchParams={{ withMktSite: true }} name="offerId" />
            </FormGroup>
          )}
        </Form>
      )}
      {mktSite?.id ? (
        <>
          <Row>
            <Col md={6}>
              <InputClipboard
                prepend={t('common:model.offer')}
                value={getNameWithId(offer)}
                className="mb-3"
                iconButton
              />
            </Col>
            <Col md={6}>
              <InputClipboard
                prepend={t('common:model.network')}
                value={getNameWithId(network)}
                className="mb-3"
                iconButton
              />
            </Col>
          </Row>
          <DocsTranslate tKey="javascriptPixel.browsePixel" code={browsePixelAsync} />
          <DocsTranslate
            tKey="javascriptPixel.conversionPixels"
            components={{
              code: (
                <div>
                  <DocsConversionStepTabset
                    withActions
                    conversionSteps={conversionSteps}
                    renderer={(conversionStep) => (
                      <RenderTranslate conversionStep={conversionStep} className="border-top" />
                    )}
                  />
                </div>
              ),
            }}
          />
        </>
      ) : (
        <MessageEmpty />
      )}
    </>
  );
};

export default DocsJavascriptPixel;
